// Парсинг .ics (iCalendar) — akce školy / využití prostor

export interface CalendarEvent {
  id: string
  subject: string
  start: {
    dateTime: string
    timeZone: string
    isAllDay?: boolean
  }
  end: {
    dateTime: string
    timeZone: string
    isAllDay?: boolean
  }
  location?: {
    displayName: string
  }
  body?: {
    content: string
    contentType: string
  }
}

/** Windows TZID (Outlook) → IANA */
const ICS_TZ_ALIASES: Record<string, string> = {
  utc: 'UTC',
  gmt: 'UTC',
  'europe/prague': 'Europe/Prague',
  'central europe standard time': 'Europe/Prague',
  'central european standard time': 'Europe/Prague',
  'w. europe standard time': 'Europe/Berlin',
  'romance standard time': 'Europe/Paris',
  'gmt standard time': 'Europe/London',
  'utc+01:00': 'Europe/Prague',
  'utc+02:00': 'Europe/Prague',
}

function normalizeIcsTimeZone(tzid?: string | null): string {
  if (!tzid) return 'Europe/Prague'
  const key = tzid.trim().toLowerCase()
  return ICS_TZ_ALIASES[key] || tzid
}

/**
 * Wall-clock čas v dané TZ → UTC ms.
 * (Iterace přes offset, spolehlivé pro Europe/Prague včetně DST.)
 */
function zonedLocalToUtcMs(
  year: number,
  month1: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timeZone: string,
): number {
  if (timeZone === 'UTC') {
    return Date.UTC(year, month1 - 1, day, hour, minute, second)
  }

  const utcGuess = Date.UTC(year, month1 - 1, day, hour, minute, second)
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })

  const readAsTz = (ms: number) => {
    const parts = dtf.formatToParts(new Date(ms))
    const map: Record<string, string> = {}
    for (const p of parts) {
      if (p.type !== 'literal') map[p.type] = p.value
    }
    return Date.UTC(
      Number(map.year),
      Number(map.month) - 1,
      Number(map.day),
      Number(map.hour) % 24,
      Number(map.minute),
      Number(map.second),
    )
  }

  const asTz = readAsTz(utcGuess)
  return utcGuess - (asTz - utcGuess)
}

function toIsoUtc(ms: number): string {
  return new Date(ms).toISOString()
}

/**
 * DTSTART/DTEND z ICS.
 * All-day: vrací půlnoc UTC daného dne; DTEND zůstává exclusive (RFC 5545) — neodečítat den.
 */
function parseICSDate(
  dateString: string,
  opts: { isAllDay?: boolean; tzid?: string | null } = {},
): { iso: string; timeZone: string; isAllDay: boolean } {
  const raw = dateString.trim()
  const isAllDay = !!opts.isAllDay || raw.length === 8

  try {
    if (isAllDay || raw.length === 8) {
      const year = parseInt(raw.substring(0, 4), 10)
      const month = parseInt(raw.substring(4, 6), 10)
      const day = parseInt(raw.substring(6, 8), 10)
      const ms = Date.UTC(year, month - 1, day, 0, 0, 0, 0)
      return { iso: toIsoUtc(ms), timeZone: 'UTC', isAllDay: true }
    }

    if (raw.length >= 15) {
      const year = parseInt(raw.substring(0, 4), 10)
      const month = parseInt(raw.substring(4, 6), 10)
      const day = parseInt(raw.substring(6, 8), 10)
      const hour = parseInt(raw.substring(9, 11) || '0', 10)
      const minute = parseInt(raw.substring(11, 13) || '0', 10)
      const second = parseInt(raw.substring(13, 15) || '0', 10)

      if (raw.endsWith('Z')) {
        return {
          iso: toIsoUtc(Date.UTC(year, month - 1, day, hour, minute, second)),
          timeZone: 'UTC',
          isAllDay: false,
        }
      }

      // TZID nebo floating → Europe/Prague (výchozí pro školní kalendář)
      const useTz = normalizeIcsTimeZone(opts.tzid || 'Europe/Prague')
      const ms = zonedLocalToUtcMs(year, month, day, hour, minute, second, useTz)
      return { iso: toIsoUtc(ms), timeZone: useTz, isAllDay: false }
    }
  } catch (error) {
    console.error('Error parsing ICS date:', dateString, error)
  }

  return { iso: new Date().toISOString(), timeZone: 'UTC', isAllDay: false }
}

function parseICS(icsContent: string): CalendarEvent[] {
  const events: CalendarEvent[] = []
  const lines = icsContent.split(/\r?\n/)

  let currentEvent: any = null
  let inEvent = false
  let currentEventIsAllDay = false

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i]

    while (i + 1 < lines.length && lines[i + 1].startsWith(' ')) {
      line += lines[i + 1].substring(1)
      i++
    }

    if (line === 'BEGIN:VEVENT') {
      inEvent = true
      currentEvent = {
        id: '',
        subject: '',
        start: { dateTime: '', timeZone: 'UTC', isAllDay: false },
        end: { dateTime: '', timeZone: 'UTC', isAllDay: false },
        location: undefined,
        body: undefined,
      }
      currentEventIsAllDay = false
      continue
    }

    if (line === 'END:VEVENT') {
      if (currentEvent && currentEvent.id && currentEvent.subject) {
        // Chybějící DTEND u all-day → +1 den (exclusive)
        if (!currentEvent.end.dateTime && currentEvent.start.dateTime) {
          const s = new Date(currentEvent.start.dateTime)
          if (currentEvent.start.isAllDay) {
            s.setUTCDate(s.getUTCDate() + 1)
            currentEvent.end.dateTime = s.toISOString()
            currentEvent.end.isAllDay = true
            currentEvent.end.timeZone = 'UTC'
          } else {
            currentEvent.end = { ...currentEvent.start }
          }
        }
        events.push({
          id: currentEvent.id,
          subject: currentEvent.subject,
          start: {
            dateTime: currentEvent.start.dateTime,
            timeZone: currentEvent.start.timeZone,
            isAllDay: currentEvent.start.isAllDay || false,
          },
          end: {
            dateTime: currentEvent.end.dateTime,
            timeZone: currentEvent.end.timeZone,
            isAllDay: currentEvent.end.isAllDay || false,
          },
          location: currentEvent.location
            ? { displayName: currentEvent.location }
            : undefined,
          body: currentEvent.body
            ? { content: currentEvent.body, contentType: 'text/plain' }
            : undefined,
        })
      }
      inEvent = false
      currentEvent = null
      currentEventIsAllDay = false
      continue
    }

    if (!inEvent) continue

    const colonIndex = line.indexOf(':')
    if (colonIndex === -1) continue

    const keyPart = line.substring(0, colonIndex)
    const value = line.substring(colonIndex + 1)
    const keyParts = keyPart.split(';')
    const key = keyParts[0]
    const params: { [key: string]: string } = {}

    for (let j = 1; j < keyParts.length; j++) {
      const param = keyParts[j].split('=')
      if (param.length >= 2) {
        params[param[0]] = param.slice(1).join('=').replace(/^["']|["']$/g, '')
      }
    }

    switch (key) {
      case 'UID':
        currentEvent.id = value
        break
      case 'SUMMARY':
        currentEvent.subject = value.replace(/\\,/g, ',').replace(/\\;/g, ';')
        break
      case 'DTSTART': {
        currentEventIsAllDay = params.VALUE === 'DATE' || value.length === 8
        const parsed = parseICSDate(value, {
          isAllDay: currentEventIsAllDay,
          tzid: params.TZID,
        })
        currentEvent.start.dateTime = parsed.iso
        currentEvent.start.timeZone = parsed.timeZone
        currentEvent.start.isAllDay = parsed.isAllDay
        break
      }
      case 'DTEND': {
        const dtendAllDayFlag: boolean = params.VALUE === 'DATE' || value.length === 8
        const treatAsAllDay: boolean = currentEventIsAllDay || dtendAllDayFlag
        currentEventIsAllDay = treatAsAllDay
        // DTEND zůstává exclusive (neodečítat den u all-day)
        const parsed = parseICSDate(value, {
          isAllDay: treatAsAllDay,
          tzid: params.TZID,
        })
        currentEvent.end.dateTime = parsed.iso
        currentEvent.end.timeZone = parsed.timeZone
        currentEvent.end.isAllDay = parsed.isAllDay
        break
      }
      case 'LOCATION':
        currentEvent.location = value.replace(/\\,/g, ',').replace(/\\;/g, ';')
        break
      case 'DESCRIPTION':
        currentEvent.body = value.replace(/\\n/g, '\n').replace(/\\,/g, ',').replace(/\\;/g, ';')
        break
    }
  }

  return events
}

function utcMidnightMs(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0)
}

/** Je událost aktivní v daný kalendářní den (Y/M0/D v „kalendářních“ UTC dnech u all-day). */
export function eventOccursOnCalendarDay(
  event: CalendarEvent,
  year: number,
  month0: number,
  day: number,
): boolean {
  const dayStart = Date.UTC(year, month0, day, 0, 0, 0, 0)
  const dayEnd = Date.UTC(year, month0, day + 1, 0, 0, 0, 0)

  const start = new Date(event.start.dateTime).getTime()
  let end = new Date(event.end.dateTime).getTime()
  if (!(end > start)) {
    end = event.start.isAllDay ? start + 86400000 : start + 1
  }

  if (event.start.isAllDay || event.end.isAllDay) {
    // Half-open [start, end) ve UTC půlnocích (DTEND exclusive dle RFC)
    return dayStart >= start && dayStart < end
  }

  // Timed: průnik [start, end) ∩ [dayStart, dayEnd)
  return start < dayEnd && end > dayStart
}

export function isMultiDayCalendarEvent(event: CalendarEvent): boolean {
  const start = new Date(event.start.dateTime)
  const end = new Date(event.end.dateTime)
  let endMs = end.getTime()
  const startMs = start.getTime()
  if (!(endMs > startMs)) {
    endMs = event.start.isAllDay ? startMs + 86400000 : startMs + 1
  }

  if (event.start.isAllDay || event.end.isAllDay) {
    const s = utcMidnightMs(start)
    const e = utcMidnightMs(new Date(endMs))
    return e - s > 86400000
  }

  const sDay = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())
  const lastInstant = new Date(endMs - 1)
  const eDay = Date.UTC(lastInstant.getFullYear(), lastInstant.getMonth(), lastInstant.getDate())
  return eDay > sDay
}

/** Pro zobrazení data all-day vždy UTC den (bez posunu TZ). */
export function eventDisplayStartParts(event: CalendarEvent): { day: number; month0: number; year: number } {
  const d = new Date(event.start.dateTime)
  if (event.start.isAllDay) {
    return { day: d.getUTCDate(), month0: d.getUTCMonth(), year: d.getUTCFullYear() }
  }
  return { day: d.getDate(), month0: d.getMonth(), year: d.getFullYear() }
}

export function formatEventTimeRange(event: CalendarEvent): string {
  if (event.start.isAllDay || event.end.isAllDay) {
    if (isMultiDayCalendarEvent(event)) {
      const s = new Date(event.start.dateTime)
      const e = new Date(event.end.dateTime)
      // Exclusive end → poslední den = end - 1
      const last = new Date(e.getTime() - 86400000)
      const fmt = (d: Date) =>
        `${d.getUTCDate()}. ${d.getUTCMonth() + 1}.`
      return `${fmt(s)} – ${fmt(last)} · celý den`
    }
    return 'Celý den'
  }

  const start = new Date(event.start.dateTime)
  const end = new Date(event.end.dateTime)
  const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' }
  return `${start.toLocaleTimeString('cs-CZ', opts)} – ${end.toLocaleTimeString('cs-CZ', opts)}`
}

export function eventOverlapsLocalDay(event: CalendarEvent, dayStartLocal: Date, dayEndLocal: Date): boolean {
  const start = new Date(event.start.dateTime).getTime()
  let end = new Date(event.end.dateTime).getTime()
  if (!(end > start)) {
    end = event.start.isAllDay ? start + 86400000 : start + 1
  }
  return start < dayEndLocal.getTime() && end > dayStartLocal.getTime()
}

export async function loadICSCalendar(
  icsUrl: string,
  startDate?: Date,
  endDate?: Date,
): Promise<CalendarEvent[]> {
  try {
    const API_BASE_URL = window.location.origin + '/api'
    const fetchUrl = `${API_BASE_URL}/calendar?url=${encodeURIComponent(icsUrl)}`

    const response = await fetch(fetchUrl, {
      method: 'GET',
      headers: { Accept: 'text/calendar, application/json' },
      mode: 'cors',
    })

    if (!response.ok) {
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json()
        throw new Error(errorData.error || `Failed to load calendar: ${response.status} ${response.statusText}`)
      }
      throw new Error(`Failed to load calendar: ${response.status} ${response.statusText}`)
    }

    const icsContent = await response.text()

    if (icsContent.trim().startsWith('{') || icsContent.trim().startsWith('[')) {
      try {
        const errorData = JSON.parse(icsContent)
        if (errorData.error) throw new Error(errorData.error)
      } catch (e) {
        if (e instanceof Error && e.message && !e.message.includes('JSON')) throw e
      }
    }

    const allEvents = parseICS(icsContent)

    if (startDate && endDate) {
      return allEvents.filter((event) => {
        const eventStart = new Date(event.start.dateTime).getTime()
        let eventEnd = new Date(event.end.dateTime).getTime()
        if (!(eventEnd > eventStart)) {
          eventEnd = event.start.isAllDay ? eventStart + 86400000 : eventStart + 1
        }
        return eventStart <= endDate.getTime() && eventEnd > startDate.getTime()
      })
    }

    return allEvents
  } catch (error: any) {
    console.error('Error loading ICS calendar:', error)
    throw new Error(`Nepodařilo se načíst kalendář: ${error.message}`)
  }
}
